import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import {
  ArrowPathIcon,
  BanknotesIcon,
  CheckCircleIcon,
  DocumentArrowUpIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const STATUS_META = {
  pendente: { label: 'Pendente', className: 'badge-warning' },
  conciliado: { label: 'Conciliado', className: 'badge-success' },
  criado: { label: 'Criado', className: 'badge-info' },
  ignorado: { label: 'Ignorado', className: 'badge-secondary' },
};

const ConciliacaoBancaria = () => {
  const [contas, setContas] = useState([]);
  const [planos, setPlanos] = useState([]);
  const [centros, setCentros] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [selectedLoteId, setSelectedLoteId] = useState('');
  const [loteAtual, setLoteAtual] = useState(null);
  const [linhas, setLinhas] = useState([]);
  const [file, setFile] = useState(null);
  const [contaId, setContaId] = useState('');
  const [perfilBanco, setPerfilBanco] = useState('auto');
  const [statusFilter, setStatusFilter] = useState('pendente');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [createLine, setCreateLine] = useState(null);
  const [createForm, setCreateForm] = useState({
    tipo: 'despesa',
    direcao_caixa: 'saida',
    plano_conta_id: '',
    centro_custo_id: '',
    status: 'confirmado',
  });
  const [createComposicoes, setCreateComposicoes] = useState([
    { tipo: 'patrimonial', plano_conta_id: '', centro_custo_id: '', valor: '', descricao: 'Principal' },
    { tipo: 'despesa', plano_conta_id: '', centro_custo_id: '', valor: '', descricao: 'Juros/encargos' },
  ]);

  const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const formatMoney = (value) => toNumber(value).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const resetCreateComposicoes = (direcao = 'saida') => {
    setCreateComposicoes(direcao === 'entrada'
      ? [
          { tipo: 'patrimonial', plano_conta_id: '', centro_custo_id: '', valor: '', descricao: 'Principal' },
          { tipo: 'receita', plano_conta_id: '', centro_custo_id: '', valor: '', descricao: 'Receita/encargos' },
        ]
      : [
          { tipo: 'patrimonial', plano_conta_id: '', centro_custo_id: '', valor: '', descricao: 'Principal' },
          { tipo: 'despesa', plano_conta_id: '', centro_custo_id: '', valor: '', descricao: 'Juros/encargos' },
        ]);
  };

  const updateCreateComposicao = (index, field, value) => {
    setCreateComposicoes((prev) => prev.map((item, idx) => {
      if (idx !== index) return item;
      const next = { ...item, [field]: value };
      if (field === 'tipo') {
        next.plano_conta_id = '';
        next.centro_custo_id = value === 'patrimonial' ? '' : next.centro_custo_id;
      }
      return next;
    }));
  };

  const addCreateComposicao = () => {
    setCreateComposicoes((prev) => [...prev, {
      tipo: createForm.direcao_caixa === 'entrada' ? 'receita' : 'despesa',
      plano_conta_id: '',
      centro_custo_id: '',
      valor: '',
      descricao: '',
    }]);
  };

  const removeCreateComposicao = (index) => {
    setCreateComposicoes((prev) => (prev.length <= 1 ? prev : prev.filter((_, idx) => idx !== index)));
  };

  const fileToBase64 = (targetFile) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve(result.split(',')[1] || '');
    };
    reader.onerror = reject;
    reader.readAsDataURL(targetFile);
  });

  const carregarBase = async () => {
    try {
      const [contasRs, planosRs, centrosRs] = await Promise.all([
        axios.get('/api/contas-bancarias'),
        axios.get('/api/plano-contas'),
        axios.get('/api/centro-custos'),
      ]);
      const contasData = Array.isArray(contasRs.data) ? contasRs.data : [];
      setContas(contasData);
      setPlanos(Array.isArray(planosRs.data) ? planosRs.data : []);
      setCentros(Array.isArray(centrosRs.data) ? centrosRs.data : []);
      if (!contaId && contasData.length > 0) {
        setContaId(contasData.find((conta) => conta.ativa)?.id || contasData[0].id);
      }
    } catch (error) {
      toast.error('Erro ao carregar cadastros para conciliação');
    }
  };

  const carregarLotes = async () => {
    try {
      const response = await axios.get('/api/conciliacao-bancaria/lotes');
      const data = Array.isArray(response.data) ? response.data : [];
      setLotes(data);
      if (!selectedLoteId && data.length > 0) {
        setSelectedLoteId(data[0].id);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar lotes');
    }
  };

  const carregarLote = async (loteId = selectedLoteId) => {
    if (!loteId) {
      setLoteAtual(null);
      setLinhas([]);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`/api/conciliacao-bancaria/lotes/${loteId}`);
      setLoteAtual(response.data?.lote || null);
      setLinhas(Array.isArray(response.data?.linhas) ? response.data.linhas : []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar lote');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarBase();
    carregarLotes();
  }, []);

  useEffect(() => {
    if (selectedLoteId) carregarLote(selectedLoteId);
  }, [selectedLoteId]);

  const handleImport = async () => {
    if (!file) return toast.error('Selecione o arquivo do extrato');
    if (!contaId) return toast.error('Selecione a conta bancária');

    setImporting(true);
    try {
      const fileBase64 = await fileToBase64(file);
      const response = await axios.post('/api/conciliacao-bancaria/importar', {
        fileBase64,
        fileName: file.name,
        conta_bancaria_id: contaId,
        perfil_banco: perfilBanco,
      });
      toast.success(`Extrato processado: ${response.data?.linhas_processadas || 0} linha(s)`);
      setFile(null);
      await carregarLotes();
      if (response.data?.lote_id) {
        setSelectedLoteId(response.data.lote_id);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || error.response?.data?.details || 'Erro ao importar extrato');
    } finally {
      setImporting(false);
    }
  };

  const handleConciliar = async (line) => {
    const lancamentoId = line.sugestao_lancamento_id;
    if (!lancamentoId) return toast.error('Esta linha não tem sugestão automática');
    setActionLoading(line.id);
    try {
      await axios.post(`/api/conciliacao-bancaria/linhas/${line.id}/conciliar`, {
        lancamento_id: lancamentoId,
      });
      toast.success('Linha conciliada');
      await Promise.all([carregarLote(), carregarLotes()]);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao conciliar linha');
    } finally {
      setActionLoading('');
    }
  };

  const handleIgnorar = async (line) => {
    if (!window.confirm('Ignorar esta linha do extrato?')) return;
    setActionLoading(line.id);
    try {
      await axios.post(`/api/conciliacao-bancaria/linhas/${line.id}/ignorar`, {
        observacao: 'Ignorado pelo usuário na conciliação',
      });
      toast.success('Linha ignorada');
      await Promise.all([carregarLote(), carregarLotes()]);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao ignorar linha');
    } finally {
      setActionLoading('');
    }
  };

  const openCreateModal = (line) => {
    const isEntrada = toNumber(line.valor) >= 0;
    setCreateLine(line);
    setCreateForm({
      tipo: isEntrada ? 'receita' : 'despesa',
      direcao_caixa: isEntrada ? 'entrada' : 'saida',
      plano_conta_id: '',
      centro_custo_id: '',
      status: 'confirmado',
    });
    resetCreateComposicoes(isEntrada ? 'entrada' : 'saida');
  };

  const handleCreateLancamento = async () => {
    if (!createLine) return;
    const valorBanco = Math.abs(toNumber(createLine.valor));
    let payload = { ...createForm };
    if (createForm.tipo === 'composto') {
      const linhasValidas = createComposicoes
        .map((item, index) => ({
          ...item,
          valor: toNumber(item.valor),
          ordem: index + 1,
        }))
        .filter((item) => item.valor > 0 || item.plano_conta_id || item.descricao);
      const totalComposicoes = linhasValidas.reduce((sum, item) => sum + toNumber(item.valor), 0);
      if (linhasValidas.length === 0) return toast.error('Informe ao menos uma composição');
      if (linhasValidas.some((item) => !item.tipo || !item.plano_conta_id || item.valor <= 0)) {
        return toast.error('Preencha tipo, conta e valor de todas as composições');
      }
      if (Math.abs(totalComposicoes - valorBanco) > 0.01) {
        return toast.error('A soma das composições deve bater com o valor do banco');
      }
      payload = {
        ...payload,
        plano_conta_id: '',
        centro_custo_id: '',
        composicoes: linhasValidas,
      };
    } else if (!createForm.plano_conta_id) {
      return toast.error('Selecione o plano de contas');
    }
    setActionLoading(createLine.id);
    try {
      await axios.post(`/api/conciliacao-bancaria/linhas/${createLine.id}/criar-lancamento`, {
        ...payload,
        descricao: createLine.descricao,
      });
      toast.success('Lançamento criado e conciliado');
      setCreateLine(null);
      await Promise.all([carregarLote(), carregarLotes()]);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao criar lançamento');
    } finally {
      setActionLoading('');
    }
  };

  const filteredLinhas = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return linhas.filter((line) => {
      if (statusFilter !== 'todos' && line.status !== statusFilter) return false;
      if (!normalizedSearch) return true;
      return [
        line.descricao,
        line.documento,
        line.sugestao_descricao,
        line.lancamento_descricao,
        String(line.valor || ''),
      ].some((value) => String(value || '').toLowerCase().includes(normalizedSearch));
    });
  }, [linhas, search, statusFilter]);

  const resumo = useMemo(() => ({
    total: linhas.length,
    pendente: linhas.filter((line) => line.status === 'pendente').length,
    conciliado: linhas.filter((line) => line.status === 'conciliado').length,
    criado: linhas.filter((line) => line.status === 'criado').length,
    ignorado: linhas.filter((line) => line.status === 'ignorado').length,
  }), [linhas]);

  const planosFiltrados = planos.filter((plano) => {
    if (createForm.tipo === 'receita') return plano.tipo === 'receita';
    if (createForm.tipo === 'despesa') return plano.tipo === 'despesa';
    if (createForm.tipo === 'patrimonial') return plano.tipo === 'patrimonio';
    return false;
  });
  const getPlanoTipoByComposicao = (tipo) => (tipo === 'patrimonial' ? 'patrimonio' : tipo);
  const getPlanosComposicao = (tipo) => planos.filter((plano) => plano.tipo === getPlanoTipoByComposicao(tipo));
  const tiposComposicaoPermitidos = createForm.direcao_caixa === 'entrada'
    ? [
        { value: 'patrimonial', label: 'Patrimonial' },
        { value: 'receita', label: 'Receita' },
      ]
    : [
        { value: 'patrimonial', label: 'Patrimonial' },
        { value: 'despesa', label: 'Despesa' },
      ];
  const totalCreateComposicoes = createComposicoes.reduce((sum, item) => sum + toNumber(item.valor), 0);

  return (
    <div className="animate-fade-in">
      <div className="mb-6 flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Conciliação Bancária</h1>
          <p className="text-gray-600">Importe o extrato sem duplicar lançamentos e confirme apenas as divergências.</p>
        </div>
        <button type="button" onClick={() => Promise.all([carregarLotes(), carregarLote()])} className="btn-secondary self-start xl:self-end">
          <ArrowPathIcon className="h-5 w-5 mr-2" />
          Atualizar
        </button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[380px_minmax(0,1fr)] gap-6 mb-6">
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold">Importar Extrato</h3>
          </div>
          <div className="card-body space-y-4">
            <div>
              <label className="form-label">Conta Bancária</label>
              <select className="form-select" value={contaId} onChange={(e) => setContaId(e.target.value)}>
                <option value="">Selecione</option>
                {contas.map((conta) => (
                  <option key={conta.id} value={conta.id}>{conta.banco} - {conta.conta}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Arquivo</label>
              <input
                type="file"
                accept=".xlsx,.xls,.csv,.ofx"
                className="form-input"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              <p className="mt-2 text-xs text-gray-500">{file?.name || 'Formatos: XLSX, CSV e OFX. XLS antigo pode exigir conversão.'}</p>
            </div>
            <div>
              <label className="form-label">Perfil</label>
              <select className="form-select" value={perfilBanco} onChange={(e) => setPerfilBanco(e.target.value)}>
                <option value="auto">Auto detectar</option>
                <option value="banco_brasil">Banco do Brasil</option>
                <option value="generico">Genérico</option>
              </select>
            </div>
            <button type="button" onClick={handleImport} className="btn-primary w-full" disabled={importing}>
              <DocumentArrowUpIcon className="h-5 w-5 mr-2" />
              {importing ? 'Processando...' : 'Importar para conciliar'}
            </button>
          </div>
        </div>

        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h3 className="text-lg font-semibold">Lotes Recentes</h3>
            <span className="text-sm text-gray-500">{lotes.length} lote(s)</span>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-3">
              {lotes.map((lote) => (
                <button
                  key={lote.id}
                  type="button"
                  onClick={() => setSelectedLoteId(lote.id)}
                  className={`rounded-lg border p-4 text-left transition ${selectedLoteId === lote.id ? 'border-primary-500 bg-primary-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-gray-900">{lote.nome_arquivo}</p>
                      <p className="mt-1 text-xs text-gray-500">{lote.conta_banco} - {lote.conta_numero}</p>
                    </div>
                    <span className="badge badge-info">{String(lote.formato || '').toUpperCase()}</span>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                    <span className="rounded bg-warning-50 px-2 py-1 text-warning-800">Pend. {lote.linhas_pendentes}</span>
                    <span className="rounded bg-success-50 px-2 py-1 text-success-800">Ok {lote.linhas_conciliadas}</span>
                    <span className="rounded bg-gray-100 px-2 py-1 text-gray-700">Ign. {lote.linhas_ignoradas}</span>
                  </div>
                </button>
              ))}
              {lotes.length === 0 && (
                <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-gray-500">
                  Nenhum extrato enviado para conciliação.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {loteAtual && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            {[
              { label: 'Linhas', value: resumo.total, icon: BanknotesIcon, className: 'bg-primary-100 text-primary-700' },
              { label: 'Pendentes', value: resumo.pendente, icon: ExclamationTriangleIcon, className: 'bg-warning-100 text-warning-700' },
              { label: 'Conciliadas', value: resumo.conciliado, icon: CheckCircleIcon, className: 'bg-success-100 text-success-700' },
              { label: 'Criadas', value: resumo.criado, icon: PlusIcon, className: 'bg-blue-100 text-blue-700' },
              { label: 'Ignoradas', value: resumo.ignorado, icon: XMarkIcon, className: 'bg-gray-100 text-gray-700' },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.label} className="card">
                  <div className="card-body">
                    <div className="flex items-center">
                      <div className={`rounded-full p-3 ${item.className}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-gray-500">{item.label}</p>
                        <p className="text-2xl font-bold text-gray-900">{item.value}</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="card">
            <div className="card-header">
              <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                <div>
                  <h3 className="text-lg font-semibold">{loteAtual.nome_arquivo}</h3>
                  <p className="text-sm text-gray-500">{loteAtual.conta_banco} - {loteAtual.conta_numero}</p>
                </div>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="todos">Todos</option>
                    <option value="pendente">Pendentes</option>
                    <option value="conciliado">Conciliados</option>
                    <option value="criado">Criados</option>
                    <option value="ignorado">Ignorados</option>
                  </select>
                  <div className="relative min-w-[260px]">
                    <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      className="form-input pl-10"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Buscar histórico, documento ou valor"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="card-body p-0">
              <div className="table-container">
                <table className="table min-w-[1100px]">
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Histórico do Banco</th>
                      <th>Valor</th>
                      <th>Status</th>
                      <th>Sugestão do Sistema</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLinhas.map((line) => {
                      const meta = STATUS_META[line.status] || STATUS_META.pendente;
                      const hasSuggestion = Boolean(line.sugestao_lancamento_id);
                      return (
                        <tr key={line.id}>
                          <td>{line.data ? new Date(`${line.data}T00:00:00`).toLocaleDateString('pt-BR') : '-'}</td>
                          <td>
                            <p className="font-medium text-gray-900">{line.descricao}</p>
                            <p className="text-xs text-gray-500">Linha {line.row_number}{line.documento ? ` | Doc. ${line.documento}` : ''}</p>
                          </td>
                          <td className={toNumber(line.valor) >= 0 ? 'font-semibold text-success-700' : 'font-semibold text-danger-700'}>
                            {toNumber(line.valor) >= 0 ? '+' : '-'} R$ {formatMoney(Math.abs(toNumber(line.valor)))}
                          </td>
                          <td><span className={`badge ${meta.className}`}>{meta.label}</span></td>
                          <td>
                            {line.lancamento_id ? (
                              <div className="text-sm">
                                <p className="font-medium text-gray-900">{line.lancamento_descricao}</p>
                                <p className="text-xs text-gray-500">Lançamento vinculado</p>
                              </div>
                            ) : hasSuggestion ? (
                              <div className="text-sm">
                                <p className="font-medium text-gray-900">{line.sugestao_descricao}</p>
                                <p className="text-xs text-gray-500">
                                  {line.sugestao_data ? new Date(`${line.sugestao_data}T00:00:00`).toLocaleDateString('pt-BR') : '-'} | R$ {formatMoney(line.sugestao_valor)} | {toNumber(line.sugestao_score).toFixed(0)}%
                                </p>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-500">Sem sugestão automática</span>
                            )}
                          </td>
                          <td>
                            {line.status === 'pendente' ? (
                              <div className="flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  className="btn-secondary text-xs py-1.5 px-2"
                                  onClick={() => handleConciliar(line)}
                                  disabled={!hasSuggestion || actionLoading === line.id}
                                >
                                  <LinkIcon className="h-4 w-4 mr-1" />
                                  Conciliar
                                </button>
                                <button
                                  type="button"
                                  className="btn-secondary text-xs py-1.5 px-2"
                                  onClick={() => openCreateModal(line)}
                                  disabled={actionLoading === line.id}
                                >
                                  Criar
                                </button>
                                <button
                                  type="button"
                                  className="btn-secondary text-xs py-1.5 px-2"
                                  onClick={() => handleIgnorar(line)}
                                  disabled={actionLoading === line.id}
                                >
                                  Ignorar
                                </button>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-500">Tratada</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {filteredLinhas.length === 0 && (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-gray-500">
                          {loading ? 'Carregando...' : 'Nenhuma linha encontrada para os filtros.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}

      {createLine && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-75 p-4">
          <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-lg bg-white">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <div>
                <h3 className="text-lg font-semibold">Criar lançamento pela linha do banco</h3>
                <p className="text-sm text-gray-500">{createLine.descricao}</p>
              </div>
              <button type="button" onClick={() => setCreateLine(null)} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2">
              <div>
                <label className="form-label">Tipo</label>
                <select
                  className="form-select"
                  value={createForm.tipo}
                  onChange={(e) => {
                    const nextTipo = e.target.value;
                    const nextDirecao = ['patrimonial', 'composto'].includes(nextTipo)
                      ? (createForm.direcao_caixa || (toNumber(createLine.valor) >= 0 ? 'entrada' : 'saida'))
                      : createForm.direcao_caixa;
                    setCreateForm({
                      ...createForm,
                      tipo: nextTipo,
                      plano_conta_id: '',
                      centro_custo_id: ['patrimonial', 'composto'].includes(nextTipo) ? '' : createForm.centro_custo_id,
                      direcao_caixa: nextDirecao,
                    });
                    if (nextTipo === 'composto') resetCreateComposicoes(nextDirecao);
                  }}
                >
                  <option value="receita">Receita</option>
                  <option value="despesa">Despesa</option>
                  <option value="patrimonial">Patrimonial</option>
                  <option value="composto">Composto</option>
                </select>
              </div>
              {['patrimonial', 'composto'].includes(createForm.tipo) && (
                <div>
                  <label className="form-label">Direção do Caixa</label>
                  <select
                    className="form-select"
                    value={createForm.direcao_caixa}
                    onChange={(e) => {
                      setCreateForm({ ...createForm, direcao_caixa: e.target.value });
                      if (createForm.tipo === 'composto') resetCreateComposicoes(e.target.value);
                    }}
                  >
                    <option value="entrada">Entrada no caixa</option>
                    <option value="saida">Saída do caixa</option>
                  </select>
                </div>
              )}
              {createForm.tipo !== 'composto' && (
              <div className={createForm.tipo === 'patrimonial' ? '' : 'md:col-span-2'}>
                <label className="form-label">{createForm.tipo === 'patrimonial' ? 'Conta Patrimonial' : 'Plano de Contas'}</label>
                <select className="form-select" value={createForm.plano_conta_id} onChange={(e) => setCreateForm({ ...createForm, plano_conta_id: e.target.value })}>
                  <option value="">Selecione</option>
                  {planosFiltrados.map((plano) => (
                    <option key={plano.id} value={plano.id}>{plano.codigo} - {plano.descricao}</option>
                  ))}
                </select>
              </div>
              )}
              {!['patrimonial', 'composto'].includes(createForm.tipo) && (
                <div>
                  <label className="form-label">Centro de Custo</label>
                  <select className="form-select" value={createForm.centro_custo_id} onChange={(e) => setCreateForm({ ...createForm, centro_custo_id: e.target.value })}>
                    <option value="">Sem centro</option>
                    {centros.map((centro) => (
                      <option key={centro.id} value={centro.id}>{centro.codigo} - {centro.descricao}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="form-label">Status</label>
                <select className="form-select" value={createForm.status} onChange={(e) => setCreateForm({ ...createForm, status: e.target.value })}>
                  <option value="confirmado">Confirmado</option>
                  <option value="pendente">Pendente</option>
                </select>
              </div>
              {createForm.tipo === 'composto' && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 md:col-span-2">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Composição</p>
                      <p className="text-xs text-gray-600">Separe principal patrimonial e resultado.</p>
                    </div>
                    <span className={`text-sm font-medium ${Math.abs(totalCreateComposicoes - Math.abs(toNumber(createLine.valor))) <= 0.01 ? 'text-success-600' : 'text-danger-600'}`}>
                      Soma: R$ {formatMoney(totalCreateComposicoes)}
                    </span>
                  </div>
                  <div className="mt-3 space-y-2">
                    {createComposicoes.map((item, index) => (
                      <div key={index} className="grid grid-cols-1 gap-2 rounded-md border border-gray-200 bg-white p-3 md:grid-cols-12">
                        <div className="md:col-span-2">
                          <label className="form-label text-xs mb-1">Tipo</label>
                          <select className="form-select" value={item.tipo} onChange={(e) => updateCreateComposicao(index, 'tipo', e.target.value)}>
                            {tiposComposicaoPermitidos.map((tipo) => (
                              <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="md:col-span-4">
                          <label className="form-label text-xs mb-1">Conta</label>
                          <select className="form-select" value={item.plano_conta_id} onChange={(e) => updateCreateComposicao(index, 'plano_conta_id', e.target.value)}>
                            <option value="">Selecione</option>
                            {getPlanosComposicao(item.tipo).map((plano) => (
                              <option key={plano.id} value={plano.id}>{plano.codigo} - {plano.descricao}</option>
                            ))}
                          </select>
                        </div>
                        <div className="md:col-span-2">
                          <label className="form-label text-xs mb-1">Valor</label>
                          <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            className="form-input"
                            value={item.valor}
                            onChange={(e) => updateCreateComposicao(index, 'valor', e.target.value)}
                          />
                        </div>
                        <div className="md:col-span-3">
                          <label className="form-label text-xs mb-1">{item.tipo === 'patrimonial' ? 'Descrição' : 'Centro / descrição'}</label>
                          {item.tipo !== 'patrimonial' && (
                            <select className="form-select mb-2" value={item.centro_custo_id} onChange={(e) => updateCreateComposicao(index, 'centro_custo_id', e.target.value)}>
                              <option value="">Sem centro</option>
                              {centros.map((centro) => (
                                <option key={centro.id} value={centro.id}>{centro.codigo} - {centro.descricao}</option>
                              ))}
                            </select>
                          )}
                          <input
                            type="text"
                            className="form-input"
                            value={item.descricao}
                            onChange={(e) => updateCreateComposicao(index, 'descricao', e.target.value)}
                            placeholder={item.tipo === 'patrimonial' ? 'Principal' : 'Juros, multa...'}
                          />
                        </div>
                        <div className="md:col-span-1 md:pt-6">
                          <button
                            type="button"
                            className="btn-secondary w-full px-2 py-2"
                            onClick={() => removeCreateComposicao(index)}
                            disabled={createComposicoes.length <= 1}
                          >
                            <XMarkIcon className="mx-auto h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <button type="button" className="btn-secondary" onClick={addCreateComposicao}>Adicionar linha</button>
                    <span className="text-xs text-gray-600">
                      Diferença: R$ {formatMoney(Math.abs(toNumber(createLine.valor)) - totalCreateComposicoes)}
                    </span>
                  </div>
                </div>
              )}
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 md:col-span-2">
                <p className="text-sm text-gray-600">Valor do banco</p>
                <p className={toNumber(createLine.valor) >= 0 ? 'text-xl font-semibold text-success-700' : 'text-xl font-semibold text-danger-700'}>
                  {toNumber(createLine.valor) >= 0 ? '+' : '-'} R$ {formatMoney(Math.abs(toNumber(createLine.valor)))}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t px-6 py-4">
              <button type="button" className="btn-secondary" onClick={() => setCreateLine(null)}>Cancelar</button>
              <button type="button" className="btn-primary" onClick={handleCreateLancamento} disabled={actionLoading === createLine.id}>
                {actionLoading === createLine.id ? 'Salvando...' : 'Criar e conciliar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConciliacaoBancaria;
